import {useState, useCallback, useRef, useMemo, type CSSProperties, type ChangeEvent, type DragEvent} from 'react'
import {Box, Typography, Button} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import {useTranslation} from 'react-i18next'
import {ALLOWED_IMAGE_TYPES, MAX_IMAGE_SIZE_BYTES} from '../../../common/constants/assetUpload'
import LoadingLogo from '../LoadingLogo'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

interface SpriteDropZoneProps {
  imageUrl: string
  /** When true, render the empty "drop here" placeholder even though `imageUrl`
   *  is set — used to signal "this is just the default fallback, replace it"
   *  while the actual scene viewport keeps rendering the default image. */
  isDefaultImage?: boolean
  /** Max allowed upload size in bytes (default 5 MB). Decals pass a tighter 100 kB cap. */
  maxSizeBytes?: number
  onApplyFile: (file: File) => Promise<void>
}

const validateImageFile = (file: File, maxSizeBytes: number): string | null => {
  if (!ALLOWED_IMAGE_TYPES.has(file.type)) return 'sprite.invalidType'
  if (file.size > maxSizeBytes) return 'sprite.tooLarge'
  return null
}

const SpriteDropZone = ({
  imageUrl,
  isDefaultImage = false,
  maxSizeBytes = MAX_IMAGE_SIZE_BYTES,
  onApplyFile,
}: SpriteDropZoneProps) => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const sx = useMemo(() => buildStyles(tokens), [tokens])

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const applyFile = useCallback(
    async (file: File) => {
      setUploadError(null)
      const error = validateImageFile(file, maxSizeBytes)
      if (error) {
        setUploadError(t(error))
        return
      }
      setUploading(true)
      try {
        await onApplyFile(file)
      } catch {
        setUploadError(t('sprite.uploadError'))
      } finally {
        setUploading(false)
      }
    },
    [onApplyFile, t, maxSizeBytes],
  )

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      await applyFile(file)
      e.target.value = ''
    },
    [applyFile],
  )

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDragOver(false)
  }, [])

  const handleDrop = useCallback(
    async (e: DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      const file = e.dataTransfer.files[0]
      if (!file || !file.type.startsWith('image/')) return
      await applyFile(file)
    },
    [applyFile],
  )

  return (
    <Box sx={sx.root}>
      <Box
        sx={{...sx.dropZone, ...(dragOver ? sx.dropZoneActive : {})}}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {imageUrl && !isDefaultImage ? (
          <Box component="img" src={imageUrl} alt={t('a11y.spritePreview')} sx={sx.imagePreview} />
        ) : (
          <Box sx={sx.emptyPreview}>
            <UploadFileIcon sx={{fontSize: 22, opacity: 0.4}} />
            <Typography sx={sx.dropText}>{t('sprite.dropHere')}</Typography>
          </Box>
        )}
        {dragOver && (
          <Box sx={sx.dropOverlay}>
            <Typography sx={sx.dropText}>{t('sprite.dropHere')}</Typography>
          </Box>
        )}
      </Box>

      <Button
        variant="outlined"
        size="small"
        startIcon={uploading ? <LoadingLogo size={14} /> : <UploadFileIcon />}
        onClick={handleUploadClick}
        disabled={uploading}
        sx={sx.uploadButton}
      >
        {uploading ? t('sprite.uploading') : t('sprite.upload')}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        style={{display: 'none'}}
      />
      <Typography sx={sx.hint}>{t('sprite.formatHint')}</Typography>
      {uploadError && <Typography sx={sx.errorText}>{uploadError}</Typography>}
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    marginBottom: '8px',
  },
  dropZone: {
    position: 'relative' as const,
    borderRadius: '6px',
    border: `2px dashed ${tokens.accordion.border}`,
    overflow: 'hidden',
    transition: 'border-color 0.2s',
    cursor: 'pointer',
  },
  dropZoneActive: {
    borderColor: tokens.text.primary,
  },
  imagePreview: {
    width: '100%',
    height: 100,
    objectFit: 'contain' as const,
    display: 'block',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
  emptyPreview: {
    width: '100%',
    height: 100,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    color: tokens.text.muted,
  },
  dropOverlay: {
    position: 'absolute' as const,
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(2px)',
  },
  dropText: {
    fontSize: 12,
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  uploadButton: {
    fontSize: 11,
    textTransform: 'none' as const,
    color: tokens.text.secondary,
    borderColor: tokens.accordion.border,
    '&:hover': {
      borderColor: tokens.text.muted,
    },
  },
  hint: {
    fontSize: 10,
    color: tokens.text.muted,
    fontStyle: 'italic' as const,
  },
  errorText: {
    fontSize: 11,
    color: '#f44336',
  } as CSSProperties,
})

export default SpriteDropZone
